// =========================================
// Version and Cache Management for PWA Updates - PRO Edition
// =========================================
// CRITICAL: APP_VERSION MUST match SW_VERSION in sw.js

const APP_VERSION = '5.12.0';
const DATA_SCHEMA_VERSION = 36;

// Keys to ALWAYS preserve during cache clear (auth-critical)
const PRESERVED_KEYS = [
  'token',
  'user',
  'auth_token',
  'session_token',
  'user_preferences',
  'theme',
  'language',
  'onboarding_completed',
  'user_id',
  'profile_id',
  'notifications_onboarding_seen',
  'notifications_skipped',
  'user_city',
  'premium_status'
];

// Keys that contain cached data (to be cleared on update)
const CACHE_PATTERNS = [
  'cached_',
  'cache_',
  'temp_',
  '_cache',
  'api_cache_',
  'last_fetch_',
  'daily_summary',
  'challenges',
  'appointments',
  'notifications_read',
  'recipes_',
  'workouts_',
  'videos_',
  'nutrition_',
  'food_logs',
  'consumed_',
  'totals_',
  'calories_'
];

// =========================================
// SAFE JSON PARSE - Never crashes
// =========================================
const safeJsonParse = (data, fallback = null) => {
  if (!data) return fallback;
  try {
    return JSON.parse(data);
  } catch (e) {
    console.warn('[Version] Invalid JSON, using fallback:', e.message);
    return fallback;
  }
};

// =========================================
// SAFE LOCALSTORAGE ACCESS - Never crashes
// =========================================
const safeGetItem = (key, fallback = null) => {
  try {
    const value = localStorage.getItem(key);
    return value !== null ? value : fallback;
  } catch (e) {
    console.warn(`[Version] Error reading ${key}:`, e.message);
    return fallback;
  }
};

const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    console.warn(`[Version] Error writing ${key}:`, e.message);
    if (e.name === 'QuotaExceededError') {
      clearNonEssentialData();
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e2) {
        return false;
      }
    }
    return false;
  }
};

const safeRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
};

// =========================================
// CLEAR NON-ESSENTIAL DATA (for quota issues)
// =========================================
const clearNonEssentialData = () => {
  console.log('[Version] Clearing non-essential data for space');
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !PRESERVED_KEYS.includes(key)) {
        const isCacheKey = CACHE_PATTERNS.some(pattern => key.includes(pattern));
        if (isCacheKey) {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach(key => safeRemoveItem(key));
  } catch (e) {
    console.error('[Version] Error clearing data:', e);
  }
};

// =========================================
// VALIDATE STORED DATA - Check for corruption
// =========================================
const validateStoredData = () => {
  const criticalKeys = ['token', 'user'];
  
  for (const key of criticalKeys) {
    const value = safeGetItem(key);
    if (value) {
      if (key === 'user') {
        const parsed = safeJsonParse(value);
        if (!parsed || typeof parsed !== 'object') {
          console.warn(`[Version] Corrupted data for ${key}, clearing`);
          safeRemoveItem(key);
        }
      }
      if (key === 'token' && (!value || value.length < 10)) {
        console.warn(`[Version] Invalid token, clearing`);
        safeRemoveItem(key);
      }
    }
  }
};

// =========================================
// PRO: DATA MIGRATION SYSTEM
// =========================================
const migrateData = (fromVersion, toVersion) => {
  console.log(`[PWA Migration] Migrating from schema ${fromVersion} to ${toVersion}`);
  
  try {
    // Schema 27: Clear old notification format
    if (fromVersion < 27) {
      safeRemoveItem('readNotificationIds');
      safeRemoveItem('lastNotificationSeen');
      console.log('[Migration] Cleared old notification data');
    }
    
    // Schema 28: Clear old food log format
    if (fromVersion < 28) {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('food_log') || key.includes('calories_'))) {
          safeRemoveItem(key);
        }
      }
      console.log('[Migration] Cleared old food log data');
    }
    
    // Schema 29-32: Progressive cache improvements
    if (fromVersion < 32) {
      clearNonEssentialData();
      console.log('[Migration] Cleared cached data for schema update');
    }
    
    // Schema 33: Full cache refresh for v5.8.0
    if (fromVersion < 33) {
      // Clear all workout/video caches
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && (key.includes('workout') || key.includes('video'))) {
          safeRemoveItem(key);
        }
      }
      console.log('[Migration] Cleared workout/video caches for v5.8.0');
    }
    
    // Schema 34: Full cache refresh for v5.9.0 - 225 verified videos
    if (fromVersion < 34) {
      // Clear all cached data for fresh start with new video catalog
      clearNonEssentialData();
      console.log('[Migration] Full cache refresh for v5.9.0 - 225 verified videos');
    }
    
    // Schema 35: v5.10.0 - Store badges update + OAuth fix
    if (fromVersion < 35) {
      clearNonEssentialData();
      console.log('[Migration] Cache refresh for v5.10.0 - Store badges + OAuth');
    }
    
    // Schema 36: v5.11.0 - Google Analytics 4 + Bug fixes
    if (fromVersion < 36) {
      clearNonEssentialData();
      console.log('[Migration] Cache refresh for v5.11.0 - GA4 integration + nutrition bug fix');
    }
    
    return true;
  } catch (e) {
    console.error('[Migration] Error:', e);
    return false;
  }
};

// =========================================
// PRO: SILENT RESET (Fallback safe)
// =========================================
const silentReset = () => {
  console.warn('[PWA] Silent reset initiated');
  
  try {
    // Save critical auth data
    const preserved = {};
    PRESERVED_KEYS.forEach(key => {
      const value = safeGetItem(key);
      if (value) preserved[key] = value;
    });
    
    // Clear everything
    try {
      localStorage.clear();
    } catch (e) {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        keys.push(localStorage.key(i));
      }
      keys.forEach(key => safeRemoveItem(key));
    }
    
    // Restore critical data
    Object.entries(preserved).forEach(([key, value]) => {
      safeSetItem(key, value);
    });
    
    // Set new version
    safeSetItem('app_version', APP_VERSION);
    safeSetItem('data_schema_version', String(DATA_SCHEMA_VERSION));
    safeSetItem('silent_reset', new Date().toISOString());
    
    // Clear all caches
    clearAllCaches();
    
    console.log('[PWA] Silent reset completed');
    return true;
  } catch (e) {
    console.error('[PWA] Silent reset failed:', e);
    return false;
  }
};

// =========================================
// PRO: CHECK FOR APP UPDATE WITH MIGRATION
// =========================================
export const checkAppUpdate = () => {
  try {
    const storedVersion = safeGetItem('app_version');
    const storedSchemaVersion = parseInt(safeGetItem('data_schema_version', '1'));
    
    const isNewVersion = storedVersion !== APP_VERSION;
    const needsMigration = storedSchemaVersion < DATA_SCHEMA_VERSION;
    const isFirstInstall = !storedVersion;
    
    if (isNewVersion || needsMigration) {
      console.log(`[PWA Update] Detected: ${storedVersion || 'new'} → ${APP_VERSION}`);
      console.log(`[PWA Update] Schema: ${storedSchemaVersion} → ${DATA_SCHEMA_VERSION}`);
      
      if (isFirstInstall) {
        initializeVersionData();
        return { updated: false, firstInstall: true };
      }
      
      // PRO: Run migration instead of clearing everything
      const migrationSuccess = migrateData(storedSchemaVersion, DATA_SCHEMA_VERSION);
      
      if (migrationSuccess) {
        // Update version markers
        safeSetItem('app_version', APP_VERSION);
        safeSetItem('data_schema_version', String(DATA_SCHEMA_VERSION));
        safeSetItem('last_update', new Date().toISOString());
        safeSetItem('previous_version', storedVersion || 'unknown');
        
        // Clear SW caches
        clearAllCaches();
        
        // Set force refresh flag
        try {
          sessionStorage.setItem('force_refresh', 'true');
          sessionStorage.setItem('update_timestamp', Date.now().toString());
        } catch (e) {}
        
        return { updated: true, previousVersion: storedVersion, migrated: true };
      } else {
        // Migration failed - do silent reset
        silentReset();
        return { updated: true, recovered: true };
      }
    }
    
    // Validate data integrity
    validateStoredData();
    return { updated: false };
    
  } catch (e) {
    console.error('[PWA Update] Error:', e);
    // PRO: Silent reset on error
    silentReset();
    return { updated: true, recovered: true };
  }
};

// =========================================
// INITIALIZE VERSION DATA (First Install)
// =========================================
const initializeVersionData = () => {
  console.log('[PWA] First install detected, initializing...');
  safeSetItem('app_version', APP_VERSION);
  safeSetItem('data_schema_version', String(DATA_SCHEMA_VERSION));
  safeSetItem('first_install', new Date().toISOString());
};

// =========================================
// CLEAR ALL CACHES (Service Worker + Browser)
// =========================================
const clearAllCaches = async () => {
  console.log('[PWA] Clearing all caches...');
  
  // 1. Clear Cache API caches
  if ('caches' in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(name => {
          // Delete all caches except current version
          if (!name.includes(APP_VERSION)) {
            console.log(`[PWA] Deleting cache: ${name}`);
            return caches.delete(name);
          }
          return Promise.resolve();
        })
      );
    } catch (e) {
      console.warn('[PWA] Cache clear error:', e);
    }
  }
  
  // 2. Force Service Worker update
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.update();
        if (registration.waiting) {
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      }
    } catch (e) {
      console.warn('[PWA] Service worker update error:', e);
    }
  }
};

// =========================================
// PRO: FORCE RELOAD (for critical updates)
// =========================================
export const forceReload = async () => {
  console.log('[PWA] Force reload requested');
  
  // Clear all caches
  await clearAllCaches();
  
  // Unregister service workers
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
    }
  }
  
  // Clear localStorage caches (keep auth)
  silentReset();
  
  // Reload page
  window.location.reload(true);
};

// =========================================
// PUBLIC API
// =========================================

export const shouldForceRefresh = () => {
  try {
    const forceRefresh = sessionStorage.getItem('force_refresh');
    if (forceRefresh === 'true') {
      sessionStorage.removeItem('force_refresh');
      return true;
    }
  } catch (e) {}
  return false;
};

export const getNoCacheHeaders = () => {
  const headers = { 'X-App-Version': APP_VERSION };
  try {
    const updateTimestamp = sessionStorage.getItem('update_timestamp');
    if (updateTimestamp) {
      headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
      headers['Pragma'] = 'no-cache';
    }
  } catch (e) {}
  return headers;
};

export const getAppVersion = () => APP_VERSION;
export const getDataSchemaVersion = () => DATA_SCHEMA_VERSION;

export const initVersionCheck = () => {
  const result = checkAppUpdate();
  
  if (result.updated) {
    console.log(`[App] Updated to v${APP_VERSION}`);
    if (result.migrated) {
      console.log('[App] Data migration successful');
    }
    if (result.recovered) {
      console.log('[App] Recovered from corrupted state');
    }
  }
  
  return result.updated;
};

export const manualCacheClear = async () => {
  console.log('[PWA] Manual cache clear requested');
  silentReset();
  await clearAllCaches();
  return true;
};

// Listen for service worker messages
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'SW_UPDATED') {
      console.log(`[App] Service Worker updated to v${event.data.version}`);
      try {
        sessionStorage.setItem('force_refresh', 'true');
      } catch (e) {}
    }
  });
}

export default {
  checkAppUpdate,
  shouldForceRefresh,
  getNoCacheHeaders,
  getAppVersion,
  getDataSchemaVersion,
  initVersionCheck,
  manualCacheClear,
  forceReload
};
